import { damp } from '../math/mathx.js';

/**
 * @file The walk cycle for a ONE-FRAME sprite.
 *
 * The player is a single 256x326 chibi PNG — there is no sprite sheet and there
 * never will be (a user decision). So the animation has to come from the
 * transform: the sprite ROCKS around its feet and BOBS vertically, which is
 * exactly the trick 2D games have used for waddling mascots forever. Two sines
 * and a damped amplitude, nothing more.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE BOB RUNS AT DOUBLE THE TILT RATE
 * ─────────────────────────────────────────────────────────────────────────────
 * One tilt cycle = lean left, lean right = TWO steps, and the body rises once
 * per step. Bobbing at the tilt frequency reads as a hop; bobbing at 2x reads as
 * footfalls. The bob is also strictly >= 0 (`0.5 - 0.5*cos`), so the sprite
 * never sinks below the ground it is standing on.
 *
 * Amplitude — not phase — is what damps to zero when the actor stops: freezing
 * the phase and fading the amplitude means the character settles upright from
 * wherever it was in the cycle, instead of snapping or finishing a lurch.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A RUNNER TAKES FASTER STEPS, NOT DEEPER LURCHES
 * ─────────────────────────────────────────────────────────────────────────────
 * `speed01` used to be clamped to 1 for BOTH terms, which was right while 1 was
 * the fastest anything could go. It stopped being right when the player learned
 * to sprint (`systems/Stamina.js`, `Player#speedMul`): the mover tops out at
 * 1.4x, both terms saturated at the walking value, and walking and running
 * produced a BIT-IDENTICAL gait — a run key with no animation tell at all,
 * which reads as an unfinished feature rather than as deferred polish.
 *
 * So {@link WalkWobble#maxScale} raises the ceiling for exactly ONE of the two:
 *
 *  • **CADENCE SCALES PAST 1.** The phase advance takes the un-clamped ratio, so
 *    at 1.4 the waddle runs `9 * (1 - 0.45 + 0.45 * 1.4)` = **10.62 rad/s**
 *    against the walk's 9 — ~18% quicker, ~3.4 footfalls a second against 2.9.
 *    THAT is the tell, and it is the honest one: a person moving 40% faster
 *    takes more steps.
 *  • **AMPLITUDE DOES NOT.** Its target is still clamped at 1, and that is a
 *    decision rather than an oversight — at 1.4 the peak lean would go from
 *    5.7 degrees to 8 and a one-frame chibi with an 8-degree list does not read
 *    as running, it reads as TIPPING OVER. The bob would gain 40% with it and
 *    the sprite would start leaving the ground.
 *
 * Frame-rate independent (`mathx.damp`), allocation-free, no `Math.random`:
 * safe to run for every enemy on the fixed step.
 *
 * @module engine/actors/WalkWobble
 */

export class WalkWobble {
  /**
   * @param {Object} [o]
   * @param {number} [o.freq=9] Tilt angular frequency in RADIANS/second (~1.4
   *   rock cycles a second, ~2.9 footfalls). Not hertz — see the header.
   * @param {number} [o.tilt=0.10] Peak lean in radians (~5.7 degrees).
   * @param {number} [o.bob=0.04] Peak vertical rise in world units.
   * @param {number} [o.damp=12] How fast the amplitude follows the "am I
   *   walking" signal. Higher = snappier start/stop.
   * @param {number} [o.speedFreq=0.45] Fraction of the frequency that scales
   *   with speed (0 = constant cadence, 1 = cadence proportional to speed).
   * @param {number} [o.maxScale=1] Ceiling on the speed ratio the CADENCE may
   *   use — the sprint. See {@link WalkWobble#maxScale}; 1 is the identity.
   * @param {number} [o.idleBreath=0] Optional idle breathing amplitude (world
   *   units) at {@link idleFreq}. Default 0 — the brief wants a hard settle to
   *   zero; scenes/actors can opt in.
   * @param {number} [o.idleFreq=1.7] Idle breathing rate, radians/second.
   */
  constructor(o = {}) {
    const {
      freq = 9,
      tilt = 0.1,
      bob = 0.04,
      damp: lambda = 12,
      speedFreq = 0.45,
      maxScale = 1,
      idleBreath = 0,
      idleFreq = 1.7,
    } = o;

    this.freq = freq;
    this.tiltAmp = tilt;
    this.bobAmp = bob;
    this.damp = lambda;
    this.speedFreq = speedFreq;
    /**
     * @type {number} How far past 1 the speed ratio may drive the CADENCE — the
     * sprint ceiling. A LIVE field: `Player#update` writes its own `speedMul`
     * here every step, so the gait quickens on the step the run key goes down
     * and settles on the step it comes up.
     *
     * ⚠ INERT AT 1 BY CONSTRUCTION, in exactly the sense `Player#armour` is
     * inert at 0 and `Player#speedMul` is inert at 1. At the default the cadence
     * clamp `s > maxScale ? maxScale : s` IS the `s > 1 ? 1 : s` that shipped,
     * and the amplitude clamp below is then a no-op on an already-clamped
     * value — so this class stays bit-exact for every caller that never writes
     * it. That matters more here than anywhere: this runs for EVERY enemy on
     * the fixed step, and hero mode's golden checksum (1878938342,
     * `tests/hero.test.mjs`) is downstream of all of them.
     *
     * ⚠ IT RAISES THE CEILING FOR THE CADENCE ONLY. The amplitude target keeps
     * its own hard clamp at 1 — see the header for why a deeper lurch is the
     * wrong tell.
     */
    this.maxScale = maxScale;
    this.idleBreath = idleBreath;
    this.idleFreq = idleFreq;

    /** @type {number} Current lean, radians. Feed to `material.rotation`. */
    this.tilt = 0;
    /** @type {number} Current rise, world units. ADD to the sprite's Y. */
    this.bob = 0;
    /** @type {number} 0..1 how "on" the walk cycle is. */
    this.amp = 0;
    /** @type {number} Cycle phase, radians. */
    this.phase = 0;
    /** @type {number} Idle breathing phase, radians. */
    this.idlePhase = 0;
  }

  /**
   * @param {number} dt Seconds (fixed step).
   * @param {number} speed01 Normalised speed, `speed / maxSpeed`. ⚠ 0..1 for
   *   every caller at the default {@link WalkWobble#maxScale}, and UN-CLAMPED
   *   past 1 for a sprinting one — pass the raw ratio and let this method do
   *   the clamping, or the sprint's cadence is thrown away before it arrives.
   *   (`Player#speed01` is still the clamped 0..1 public reading; it is
   *   deliberately no longer what feeds this call.)
   * @param {boolean} moving Whether the actor is under power. Passing `false`
   *   with a non-zero `speed01` (sliding to a stop) still settles the wobble,
   *   which is what you want — the legs stop before the momentum does.
   * @returns {this}
   */
  update(dt, speed01, moving) {
    // TWO clamps on one ratio, and the split IS the feature (see the header).
    // `r` may reach `maxScale` and drives the CADENCE; `s` is `r` held at 1 and
    // drives the AMPLITUDE. At the default `maxScale` 1 the first line is the
    // clamp that shipped and the second cannot fire, so this is bit-exact.
    const r = speed01 < 0 ? 0 : speed01 > this.maxScale ? this.maxScale : speed01;
    const s = r > 1 ? 1 : r;
    const target = moving ? s : 0;

    this.amp = damp(this.amp, target, this.damp, dt);
    // Below a hair's width the sine is invisible; snapping avoids carrying a
    // denormal-ish value around forever.
    if (this.amp < 1e-4 && target === 0) this.amp = 0;

    if (moving) {
      // ⚠ `r`, NOT `s` — this is the one place the sprint is visible. Tidying
      // it back to `s` deletes the entire animation tell for the run and leaves
      // a green suite behind, because at `maxScale` 1 the two are the same
      // number.
      this.phase += dt * this.freq * (1 - this.speedFreq + this.speedFreq * r);
      if (this.phase > TAU) this.phase -= TAU;
    }

    this.tilt = Math.sin(this.phase) * this.tiltAmp * this.amp;
    this.bob = (0.5 - 0.5 * Math.cos(this.phase * 2)) * this.bobAmp * this.amp;

    if (this.idleBreath > 0) {
      this.idlePhase += dt * this.idleFreq;
      if (this.idlePhase > TAU) this.idlePhase -= TAU;
      // Fades in as the walk fades out, so the two never stack.
      this.bob += (0.5 - 0.5 * Math.cos(this.idlePhase)) * this.idleBreath * (1 - this.amp);
    }

    return this;
  }

  /** Hard reset — teleports, respawns, level switches. */
  reset() {
    this.tilt = 0;
    this.bob = 0;
    this.amp = 0;
    this.phase = 0;
    this.idlePhase = 0;
  }
}

const TAU = Math.PI * 2;
